import dayjs from 'dayjs'
import isBetweenPlugin from 'dayjs/plugin/isBetween'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'

export function getFormattedDate(dateTime, format = null) {
  const parsed = dayjs(dateTime)
  const date = format ? parsed.format(format) : parsed.format('MMM D, YYYY')
  return `${date}`
}

/**
 * @name getFormattedDateTime
 * @description returns Date time formatted
 * @param {object} datetime
 * @returns {string} Ex. May 26, 2021 at 11:03 am
 */
export function getFormattedDateTime(dateTime) {
  const parsed = dayjs(dateTime)
  const date = parsed.format('MMM D, YYYY')
  const time = parsed.format('h:mm a')
  return `${date}, at ${time}`
}

/**
 * @name getTimeTo
 * @description Given datetime returns from now to datetime
 * @param {object} datetime
 * @returns {string}
 */
export function getTimeTo(datetime) {
  dayjs.extend(relativeTime)
  dayjs.extend(isTomorrow)

  return dayjs(datetime).isTomorrow() ? 'tomorrow' : dayjs().to(datetime)
}

/**
 * @name getTimeFromNow
 * @description Given datetime returns relative time from datetime
 * @param {object} datetime
 * @param {bool} withoutSuffix
 * @returns {string}
 */
export function getTimeFromNow(datetime, withoutSuffix) {
  dayjs.extend(relativeTime)
  return dayjs().from(datetime, withoutSuffix)
}

/**
 * @name getDateDifference
 * @description returns the difference between two dates based on the unit
 * @param {object} date1
 * @param {object} date2
 * @param {string} unit
 * @returns {string}
 */
export function getDateDifference(date1, date2, unit) {
  return dayjs(date1).diff(dayjs(date2), unit)
}

/**
 * @name isDate
 * @description function to compare date with date
 * @param {object} dateTime
 * @returns {object}
 */
export function isDate(datetime) {
  dayjs.extend(isBetweenPlugin)
  return {
    before: comparer => dayjs(datetime).isBefore(comparer),
    sameDayAs: comparer => dayjs(datetime).isSame(comparer, 'day'),
    inThePast: () => dayjs().isAfter(datetime),
    after: comparer => dayjs(datetime).isAfter(comparer),
    between: (from, to, included = false) =>
      dayjs(datetime).isBetween(from, to, null, included && '[]'),
  }
}

/**
 * @name parseUTC
 * @description parse datetime to UTC timezone
 * @param {object} dateTime
 * @returns {string}
 */
export function parseUTC(datetime) {
  dayjs.extend(utc)
  return dayjs(datetime)
    .utc()
    .local()
    .toDate()
}

/**
 * @name Format Seconds
 * @description Given X amount of seconds, returns the formatted time as HH:mm:ss
 * @param {number} seconds
 */
export function formatSeconds(seconds) {
  const totalHours = Math.floor(seconds / (60 * 60))
  let totalSeconds = seconds - totalHours * 60 * 60

  const totalMinutes = Math.floor(totalSeconds / 60)
  totalSeconds -= totalMinutes * 60

  return `${totalHours
    .toString()
    .padStart(2, '0')}:${totalMinutes
    .toString()
    .padStart(2, '0')}:${totalSeconds.toString().padStart(2, '0')}`
}

/**
 * @name generalFormat
 * @description Format a date object according to the string passed in
 * @param {object} dateTime
 * @param {string} dateFormat
 * @returns {string}
 */
export function generalTimeFormat(dateTime, dateFormat) {
  if (typeof dateTime === 'string') {
    return dayjs(dateTime).format(dateFormat)
  }
  return dateTime.format(dateFormat)
}

/**
 * @name setInitialTime
 * @description Set minutes, and seconds values of a date object instance
 * @param {number} minute
 * @param {number} second
 * @returns {object}
 */
export function setInitialTime({ minute, second }) {
  return dayjs()
    .set('minute', minute)
    .set('second', second)
}

/**
 * @name getCurrentTime
 * @description return the current value of a time unit key from DayJS object
 * @param {object} dateTime
 * @param {string} unit
 * @returns {number}
 */
export function getCurrentTime({ dateTime, unit }) {
  const result = {
    hour: dateTime.hour(),
    minute: dateTime.minute(),
    second: dateTime.second(),
  }
  return result[unit]
}

/**
 * @name setDateUnit
 * @description Takes a date, applies a {unit} add and returns it
 * @param {Date} date -- date to transform
 * @param {string} unit -- possible values: year, month, day, hour, minute, seconds
 * @param {number} value -- the number to add to the unit. Can also use negative number
 * @returns {dayjs date}
 */
export function setDateUnit(date, unit, value) {
  return dayjs(date)
    .add(value, unit)
    .toDate()
}

/**
 * @name manipulateDatetimeTo
 * @description return an object with options to manipulate the given datetime
 * @param {object} datetime
 * @param {string} unit
 * @param {string} period
 * @returns {object}
 */
export function manipulateDatetimeTo(datetime) {
  return {
    add: (unit, period) => dayjs(datetime).add(unit, period),
    subtract: (unit, period) => dayjs(datetime).subtract(unit, period),
    startOf: period => dayjs(datetime).startOf(period),
    endOf: period => dayjs(datetime).endOf(period),
  }
}

export function resetUTC(dateTime) {
  dayjs.extend(utc)
  return dayjs(dateTime).utc(false)
}

// This was added because we had may places using the same value, if we need for variants than this one
// we can discuss a better solution
export const TWENTY_FOUR_HOURS_IN_MS = 1000 * 60 * 60 * 24
export const ONE_HOUR_IN_MS = 60 * 60 * 1000
export const ONE_MINUTE_IN_MS = 60 * 1000
